import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Brain, Send, Loader2, Sparkles, ChevronDown } from 'lucide-react';
import { BrandLoader } from '../components/BrandLoader';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { DiagnosisResponse } from '../components/DiagnosisResponse';
import { VoiceInput } from '../components/VoiceInput';
import { PageHeader } from '../components/PageHeader';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export const DiagnosisPage = () => {
  const [symptoms, setSymptoms] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  
  // Refs for smooth scrolling
  const conversationRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive (only if user is near bottom)
  useEffect(() => {
    if (isNearBottom && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [conversation, loading, isNearBottom]);

  // Smooth scroll to bottom
  const scrollToBottom = useCallback((behavior = 'smooth') => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior, block: 'end' });
    }
  }, []);

  // Handle scroll events to show/hide scroll button
  const handleScroll = useCallback(() => {
    if (conversationRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = conversationRef.current;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      // Show button if more than 200px from bottom
      setShowScrollButton(distanceFromBottom > 200);
      // Track if user is near bottom (within 100px)
      setIsNearBottom(distanceFromBottom < 100);
    }
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!symptoms.trim()) return;

    const userMessage = {
      role: 'user',
      content: symptoms,
      timestamp: new Date().toISOString()
    };

    setConversation(prev => [...prev, userMessage]);
    setLoading(true);
    
    // Scroll to bottom immediately when submitting
    setTimeout(() => scrollToBottom('smooth'), 100);

    try {
      const response = await axios.post(`${API}/diagnosis/analyze`, {
        symptoms,
        additional_info: '',
        patient_id: null
      });

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        userQuery: symptoms,
        timestamp: new Date().toISOString()
      };

      setConversation(prev => [...prev, aiMessage]);
      setSymptoms('');
      
      // Scroll to see the response
      setTimeout(() => scrollToBottom('smooth'), 200);
    } catch (error) {
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
    }
  };

  const handleVoiceTranscription = (text) => {
    setSymptoms(text);
    setTimeout(() => {
      handleSubmit();
    }, 300);
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (symptoms.trim() && !loading) {
        handleSubmit(e);
      }
    }
  };

  return (
    <div data-testid="diagnosis-page" className="flex flex-col h-[calc(100vh-96px-var(--regulatory-h))] lg:h-[calc(100vh-76px-var(--regulatory-h))]">
      {/* Header */}
      <div className="mb-3 sm:mb-4 flex-shrink-0">
        <PageHeader icon={Brain} title="Fiche Diagnostic" subtitle="Référence documentaire basée sur les recommandations françaises" gradient="from-indigo-600 to-purple-600" testId="diagnosis-header" />
      </div>

      {/* Conversation Area (grows, scrolls) */}
      <div className="relative flex-1 min-h-0 mb-2.5 sm:mb-4">
        <div 
          ref={conversationRef}
          onScroll={handleScroll}
          className="glassmorphic rounded-2xl p-2.5 sm:p-6 h-full overflow-y-auto scroll-smooth"
          style={{
            scrollBehavior: 'smooth',
            overscrollBehavior: 'contain'
          }}
        >
          {conversation.length === 0 ? (
            <div data-testid="empty-conversation" className="h-full flex items-center justify-center py-12">
              <div className="text-center animate-fade-in">
                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center glow-indigo">
                  <Sparkles className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">Assistant IA Prêt</h3>
                <p className="text-slate-400 max-w-md mx-auto">
                  Décrivez la situation clinique et je vous aiderai avec une analyse basée sur les recommandations françaises (HAS, Collèges, Ameli).
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {conversation.map((message, index) => (
                <div
                  key={index}
                  data-testid={`message-${message.role}-${index}`}
                  className={`flex min-w-0 ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-up`}
                  style={{
                    animationDelay: `${index * 50}ms`,
                    animationFillMode: 'backwards'
                  }}
                >
                  <div
                    className={`rounded-2xl min-w-0 transition-all duration-300 ${
                      message.role === 'user'
                        ? 'max-w-[88%] sm:max-w-[90%] p-3 sm:p-4 bg-blue-600 text-white shadow-lg shadow-blue-600/20 text-[15px] sm:text-base'
                        : 'w-full max-w-full sm:max-w-[90%] p-2.5 sm:p-4 bg-slate-900/60 border border-slate-800 text-slate-200'
                    }`}
                  >
                    {message.role === 'assistant' ? (
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-2 sm:mb-3 text-indigo-400">
                          <Brain className="w-4 h-4" />
                          <span className="text-xs font-medium">Hippocrate MG</span>
                        </div>
                        <DiagnosisResponse content={message.content} userQuery={message.userQuery} />
                      </div>
                    ) : (
                      <p className="whitespace-pre-wrap break-words">{message.content}</p>
                    )}
                  </div>
                </div>
              ))}
              
              {/* Loading indicator */}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-900/60 border border-slate-800 rounded-2xl px-6 py-2">
                    <BrandLoader label="Analyse en cours…" size={64} />
                  </div>
                </div>
              )}
              
              {/* Scroll anchor */}
              <div ref={messagesEndRef} className="h-1" />
            </div>
          )}
        </div>
        
        {/* Scroll to bottom button */}
        {showScrollButton && (
          <button
            onClick={() => scrollToBottom('smooth')}
            data-testid="scroll-to-bottom-fab"
            aria-label="Défiler vers le bas"
            className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 w-11 h-11 flex items-center justify-center bg-indigo-600 hover:bg-indigo-500 text-white rounded-full shadow-lg shadow-indigo-600/30 transition-all duration-300 hover:scale-110 active:scale-95 animate-bounce-subtle z-10"
            title="Défiler vers le bas"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Input Form (stays at the bottom) */}
      <form onSubmit={handleSubmit} className="glassmorphic rounded-2xl p-2.5 sm:p-4 flex-shrink-0">
        <textarea
          ref={inputRef}
          data-testid="symptoms-input"
          value={symptoms}
          onChange={(e) => setSymptoms(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Symptômes, tableau clinique, antécédents..."
          className="w-full px-3.5 sm:px-4 py-2.5 sm:py-3 bg-slate-950/50 border border-slate-800 rounded-xl text-slate-200 text-[15px] sm:text-base placeholder:text-slate-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-h-[56px] sm:min-h-[80px] resize-none transition-all duration-200"
          required
        />
        <div className="flex flex-row gap-2 sm:gap-3 mt-2 sm:mt-3">
          <VoiceInput 
            onTranscription={handleVoiceTranscription}
            placeholder="Dicter la recherche"
            disabled={loading}
            className="flex-shrink-0"
          />
          <Button
            data-testid="analyze-button"
            type="submit"
            disabled={loading || !symptoms.trim()}
            className="flex-1 min-h-[48px] rounded-xl px-4 sm:px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-medium transition-all duration-300 shadow-[0_4px_14px_0_rgba(99,102,241,0.39)] hover:shadow-[0_6px_20px_rgba(99,102,241,0.5)] hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                <span className="hidden sm:inline">Analyse en cours...</span>
                <span className="sm:hidden">Analyse…</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5 mr-2" />
                Analyser
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes bounce-subtle {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.4s ease-out forwards;
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
        
        .animate-bounce-subtle {
          animation: bounce-subtle 2s ease-in-out infinite;
        }
        
        /* Smooth scrollbar styling */
        .scroll-smooth::-webkit-scrollbar {
          width: 8px;
        }
        
        .scroll-smooth::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.3);
          border-radius: 4px;
        }
        
        .scroll-smooth::-webkit-scrollbar-thumb {
          background: rgba(99, 102, 241, 0.4);
          border-radius: 4px;
          transition: background 0.2s;
        }
        
        .scroll-smooth::-webkit-scrollbar-thumb:hover {
          background: rgba(99, 102, 241, 0.6);
        }
      `}</style>
    </div>
  );
};
